import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  PopulationType,
  ProviderSummaryTypes,
  getPremiumCareContent,
} from '../../../../../common/Constants';
import { FeatureFlags } from '../../../../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { PSXIcon } from '../../../../../common/PSXIcon';
import { getFeatureFlag } from '../../../../../common/Utils';
import { getTierProviderEligibility } from '../../../../../common/Utils/memberUtils/memberUtils';
import { useCoverageType } from '../../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../../hooks/useSmartChoice';
import { CustomPreferredPolicy } from '../../../../../models/Lagoon';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import {
  formatProviderStartDate,
  isProviderStartDateInFuture,
} from '../../../../../utils/date.utils';
import {
  returnSuppressFlag,
  suppressCostEstimatesForPolicies,
} from '../../../../../utils/featureSuppress';
import {
  UserDDPCode,
  getSuppressFacilityFlags,
  isShowPreferredFacilityBadge,
  isUserDDPEligible,
} from '../../../../../utils/providerDetails.utils';
import {
  getCurrentMember,
  getCustomPreferredPolicyData,
  getIsOxfordAndUSP,
  getLoggedInMember,
  getNetworkIdsForPES,
  getSitusState,
} from '../../../../../utils/user.utils';
import { getLanguage } from '../../../context/Internationalization/helpers';
import { PreferredProviderLabels } from './PreferredProviderLabels';
import {
  CostText,
  PrintTierInfoBox,
  PrintTierTwoInfoBox,
  PrintViewProviderDetailsXSText,
  PrintViewProviderTierOneText,
  ProviderIdText,
  SmartChoiceText,
} from './PrintView.style';
import { PrintPreviewTierOneIcon } from './TierOneIcon';
import { PrintPreviewTierTwoIcon } from './TierTwoIcon';

type Props = {
  provider: {
    recommendationDetails?: {
      totalRecommendationScore?: number;
    };
    numberOfReviews?: string;
    acceptingNewPatients: boolean;
    providerName: string;
    providerId: string;
    primaryDegrees: string[];
    speciality: string;
    organizationType: string;
    providerType?: string;
    networkStartDate: string;
    networkStatus: string;
    distance: string;
    isPreferredLab?: boolean;
    isFreeStandingFacility?: boolean;
    ddpCode?: string;
    healthGradeRating?: string;
    isTieredProvider: boolean;
    isTier2Provider: boolean;
    isGreenDiamondProvider?: boolean;
    isPremiumCarePhysician?: string;
    virtualCareOffered?: boolean;
    languagesSpoken: string[];
    website: string;
    address: {
      postalCode: string;
      district: string;
      line: string[];
      state: string;
      city: string;
      locationName: string;
    };
    firstName?: string;
    middleName?: string;
    lastName?: string;
    costIndicator?: string;
    phones: {
      phone: string[];
    };
  };
};

export const ProviderDetails = ({ provider }: Props) => {
  const { t } = useTranslation();

  const getCost = (cost: string | undefined) => {
    switch (cost) {
      case '$$$':
        return `$$$ - ${t('COST.ABOVE_AVERAGE')}`;
      case '$$':
        return `$$ - ${t('COST.AVERAGE')}`;
      case '$':
        return `$ - ${t('COST.BELOW_AVERAGE')}`;
      default:
        return '';
    }
  };
  const currentMember = getCurrentMember();
  const isSmartChoice = useSmartChoice();
  const isCNS = currentMember?.isCnSMember;
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();

  const [
    costEnableFlag,
    showGreenDiamondTag,
    showGreenDiamondPrintPdf,
    preferredFacilityFlag,
    oxfordPlnFlag,
    showTier2Badge,
    displayInNetworkBadgeEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.GREEN_DIAMOND_IDENTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.GREEN_DIAMOND_PRINT_PDF_IDENTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.PREFERRED_FACILITY_IDENTIFICATION,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_IN_NETWORK_BADGE_FUTURE_START_DATE,
  ]);

  const customPreferredPoliciesData: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();

  const { lob } = useChipStore(
    useShallow((state: ChipState) => ({
      lob: state.lob,
    }))
  );

  const coverageType = useCoverageType();

  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );

  const situsState = getSitusState(currentMember, lob);

  const customPreferredContent = getCustomPreferredPolicyData(
    customPreferredPoliciesData,
    currentMember?.policyNumber,
    reciprocityId,
    situsState
  );

  const preferredProviderIcon =
    customPreferredContent?.icon ??
    Constants.PREFERRED_PROVIDER_BENEFITS.GREEN_DIAMOND;

  const preferredProviderLabel =
    customPreferredContent?.badgeLabel ??
    Constants.PREFERRED_PROVIDER_BENEFITS.HEADING;

  const greenDiamondProvider = provider?.isGreenDiamondProvider || false;

  const isGreenDiamondIcon =
    preferredProviderIcon ===
    Constants.PREFERRED_PROVIDER_BENEFITS.GREEN_DIAMOND;
  // suppress premium care for M&R COSMOS
  const suppressPremiumCare =
    currentMember?.population === PopulationType.COSMOS ||
    currentMember?.isCnSMember ||
    returnSuppressFlag(ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE);

  const suppressCostEstimates = suppressCostEstimatesForPolicies(
    coverageType,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES_EMPIRE
  );
  const suppressFacilityFlags = getSuppressFacilityFlags();
  const {
    suppressPreferredFacilityPLN,
    suppressPreferredFacilityDDP,
    suppressPreferredFacilityFSF,
  } = suppressFacilityFlags;

  const ddpCodeUser = getLoggedInMember()?.ddpCode;

  const getPreferredFacilities = (provider) => {
    const {
      ddpCode,
      isFreeStandingFacility,
      isPreferredLab,
      isOxPreferredLab,
    } = provider || {};

    const { isOXMember } = getIsOxfordAndUSP(
      currentMember,
      coverageType,
      featureFlags
    );

    const showPreferredContent = isShowPreferredFacilityBadge(
      suppressFacilityFlags,
      preferredFacilityFlag,
      ddpCode,
      ddpCodeUser,
      isPreferredLab,
      isOxPreferredLab,
      isOXMember,
      isFreeStandingFacility,
      oxfordPlnFlag
    );

    const showPLN = !suppressPreferredFacilityPLN && !!isPreferredLab;
    const showDDP =
      !suppressPreferredFacilityDDP &&
      !!isUserDDPEligible(ddpCodeUser as UserDDPCode) &&
      !!ddpCode;
    const showFSF = !suppressPreferredFacilityFSF && !!isFreeStandingFacility;
    const showOxford = oxfordPlnFlag && isOxPreferredLab && isOXMember;

    return { showPreferredContent, showPLN, showDDP, showFSF, showOxford };
  };
  const { showPreferredContent, showPLN, showDDP, showFSF, showOxford } =
    getPreferredFacilities(provider);

  const locale = getLanguage().code;

  const showTierOneFlag = getFeatureFlag(
    featureFlags,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE
  );

  const isDisplayTier1Identifier =
    showTierOneFlag &&
    getTierProviderEligibility(currentMember, provider?.isTieredProvider);

  const shouldDisplayTier2Label =
    showTier2Badge &&
    currentMember.isIFPTieredPlan &&
    provider?.isTier2Provider;

  const premiumCareData = getPremiumCareContent(locale);
  const premiumCarePhysician = premiumCareData?.premiumCarePhysician;

  const displayInNetworkBadge = isProviderStartDateInFuture(
    provider?.networkStartDate
  );

  return (
    <Flex
      css={{
        'abyss-flex-root': {
          alignItems: 'flex-end',
          display: 'flex',
          flex: '1',
          flexWrap: 'nowrap',
          gap: '2px',
        },
      }}
      direction="column"
    >
      {isSmartChoice && (
        <SmartChoiceText data-testid="smart-choice-score">
          {`${t('SMART_CHOICE.SMART_CHOICE')} ${
            provider?.recommendationDetails?.totalRecommendationScore
          }%`}
        </SmartChoiceText>
      )}
      {displayInNetworkBadgeEnabled && displayInNetworkBadge ? (
        <PrintViewProviderDetailsXSText color="#333333">
          {`${t(
            'DISPLAY_IN_NETWORK_BADGE.IN_NETWORK_BADGE_PCP'
          )} ${formatProviderStartDate(provider?.networkStartDate)}`}
        </PrintViewProviderDetailsXSText>
      ) : (
        <PrintViewProviderDetailsXSText color="#333333">
          {provider.networkStatus === 'INN'
            ? t('SHARE_PDF.In-network')
            : t('SHARE_PDF.Out-of-network')}
        </PrintViewProviderDetailsXSText>
      )}
      {provider?.providerType !== ProviderSummaryTypes.ORGANIZATION && (
        <PrintViewProviderDetailsXSText color="#333333">
          {provider?.acceptingNewPatients
            ? t('SHARE_PDF.Accepting new patients')
            : t('SHARE_PDF.Not accepting new patients')}
        </PrintViewProviderDetailsXSText>
      )}
      <PreferredProviderLabels
        providerType={provider?.providerType as ProviderSummaryTypes}
        showDDP={showDDP}
        showFSF={showFSF}
        showOxford={showOxford}
        showPLN={showPLN}
        showPreferredContent={showPreferredContent}
      />
      {!isCNS && isDisplayTier1Identifier ? (
        <Flex>
          <PrintTierInfoBox
            data-auto-testId="print-preview-tier-one-indicator"
            data-testId="print-preview-tier-one-indicator"
          >
            <PrintPreviewTierOneIcon />
            <PrintViewProviderTierOneText color="#333333">
              {t('SHARE_PDF.Tier 1 Provider')}
            </PrintViewProviderTierOneText>
          </PrintTierInfoBox>
        </Flex>
      ) : null}
      {!provider?.isTieredProvider && shouldDisplayTier2Label ? (
        <Flex>
          <PrintTierTwoInfoBox
            data-auto-testId="print-preview-tier-two-indicator"
            data-testId="print-preview-tier-two-indicator"
          >
            <PrintPreviewTierTwoIcon />
            <PrintViewProviderDetailsXSText color="#333333">
              {t('SHARE_PDF.Tier 2 Provider')}
            </PrintViewProviderDetailsXSText>
          </PrintTierTwoInfoBox>
        </Flex>
      ) : null}
      {isGreenDiamondIcon &&
      greenDiamondProvider &&
      showGreenDiamondTag &&
      showGreenDiamondPrintPdf ? (
        <Flex style={{ whiteSpace: 'pre' }}>
          <PSXIcon icon={`colorless_${preferredProviderIcon}`} size={'16'} />

          <PrintViewProviderDetailsXSText
            color="#333333"
            css={{
              '&.abyss-text-root': {
                lineHeight: '18px',
              },
            }}
          >
            {preferredProviderLabel}
          </PrintViewProviderDetailsXSText>
        </Flex>
      ) : null}
      {!suppressPremiumCare && provider?.isPremiumCarePhysician === 'YES' ? (
        <Flex style={{ whiteSpace: 'pre' }}>
          <IconMaterial color="black" icon="favorite" size={'12'} />
          <IconMaterial color="black" icon="favorite" size={'12'} />
          <PrintViewProviderDetailsXSText color="#333333">
            {` ${premiumCarePhysician} *`}
          </PrintViewProviderDetailsXSText>
        </Flex>
      ) : null}
      {provider?.virtualCareOffered ? (
        <Flex>
          <PrintViewProviderDetailsXSText color="#333333">
            {t('SHARE_PDF.Offers virtual visits')}
          </PrintViewProviderDetailsXSText>
        </Flex>
      ) : null}
      {costEnableFlag && !suppressCostEstimates && provider.costIndicator ? (
        <CostText color="#333333">{getCost(provider.costIndicator)}</CostText>
      ) : null}
      {provider?.providerId && (
        <Flex style={{ whiteSpace: 'pre' }}>
          <PrintViewProviderDetailsXSText color="#333333">
            {provider?.providerType === ProviderSummaryTypes.ORGANIZATION
              ? t('SHARE_PDF.Facility ID')
              : t('SHARE_PDF.Provider ID')}
          </PrintViewProviderDetailsXSText>
          <ProviderIdText color="#333333">
            {` ${provider?.providerId}`}
          </ProviderIdText>
        </Flex>
      )}
    </Flex>
  );
};
